import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchSignInMethodsForEmail } from "firebase/auth";
import { auth } from "../../../../config/firebaseConfig";
import { GAEventTrack } from "../../../../utils/hooks/GA_events";
import { useDispatch } from "react-redux";
import { register } from "../../../../redux/features/authSlice";
import { sendEmailHandler } from "../../../../utils/helperFunctions/sendEmailHandler";
import { schoolRegisterSuccess } from "../../../../constant/emailTemplates/schoolRegisterSuccess";
import envConfig from "../../../../config/envConfig";
import { getCountryCallingCode } from "libphonenumber-js";
import SchoolRegisterStep1 from "./SchoolRegisterStep1";
import SchoolRegisterStep2 from "./SchoolRegisterStep2";

/**
 * SchoolRegisterFormStep - A 2-step school registration form component
 *
 * Step 1: Contact Person Info + School Basic Info + Fee Information
 * Step 2: School Address + School Phone + Logo + Principal Info (optional)
 *
 * Features:
 * - Progressive validation per step
 * - Data persistence between steps
 * - Step navigation with progress indicator
 * - Complete form submission on Step 2
 */

const SchoolRegisterFormStep = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [step1Data, setStep1Data] = useState({});
  const [step2Data, setStep2Data] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState(null);

  // Step 1 data
  const [contactPersonName, setContactPersonName] = useState("");
  const [contactPersonEmail, setContactPersonEmail] = useState("");
  const [contactPersonPhone, setContactPersonPhone] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [schoolEmail, setSchoolEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [schoolFee, setSchoolFee] = useState(null);

  // Step 2 data
  const [schoolAddress, setSchoolAddress] = useState("");
  const [address, setAddress] = useState({});
  const [schoolPhone, setSchoolPhone] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoRef, setLogoRef] = useState(null);
  const [principalName, setPrincipalName] = useState("");
  const [principalEmail, setPrincipalEmail] = useState("");
  const [principalPhone, setPrincipalPhone] = useState("");

  const practidoFee = 99;

  const handleStep1Next = (step1FormData) => {
    setStep1Data(step1FormData);
    setCurrentStep(2);
  };

  const handleStep2Back = () => {
    setCurrentStep(1);
  };

  const createAuthWithEmailAndPass = async (formData) => {
    const uniqueCode = `${formData.schoolName
      .split(" ")
      .map((i) => i[0].toUpperCase())
      .slice(0, 3)
      .join("")}_${formData.schoolPhone.slice(-4)}_${Math.random()
      .toString(36)
      .slice(-4)}`;

    const countryPhoneCode = getCountryCallingCode(address?.country?.id);

    try {
      const schoolData = {
        accountType: "school",
        principalName: formData.principalName,
        principalEmail: formData.principalEmail,
        principalPhone: countryPhoneCode + formData.principalPhone,
        contactPersonName: formData.contactPersonName,
        contactPersonEmail: formData.contactPersonEmail,
        contactPersonPhone: countryPhoneCode + formData.contactPersonPhone,
        schoolName: formData.schoolName,
        schoolEmail: formData.schoolEmail,
        schoolPhone: countryPhoneCode + formData.schoolPhone,
        schoolAddress: {
          address: formData.schoolAddress,
          city: address?.city?.value,
          state: address?.state?.value,
          country: address?.country?.value,
          pin: formData.pincode,
        },
        fee: {
          schoolFee: formData.schoolFee,
          practidoFee,
          total: formData.schoolFee + practidoFee,
        },
        schoolLogoUrl: formData.logoUrl,
        uniqueCode: uniqueCode,
      };

      await dispatch(
        register({
          name: formData.schoolName,
          email: formData.schoolEmail,
          password: formData.password,
          photoUrl: formData.logoUrl,
          docId: uniqueCode,
          data: schoolData,
        })
      );

      GAEventTrack({
        event: "school_reg_auth_added",
        data: {
          uid: auth?.currentUser?.uid,
          userEmail: formData.schoolEmail,
        },
      });

      return { uniqueCode };
    } catch (error) {
      throw new Error(error);
    }
  };

  const sendSuccessMail = async (name, email, schoolUniqueCode) => {
    const { subject, emailBody } = schoolRegisterSuccess({
      schoolName: name,
      emailId: email,
      parentRegistrationLink: `${envConfig?.appUrl}register/${schoolUniqueCode}`,
      schoolFee: schoolFee,
    });

    await sendEmailHandler({
      emailId: email,
      subject,
      body: emailBody,
    });

    GAEventTrack({
      event: "school_success_email_sent",
      data: {
        uid: auth?.currentUser?.uid,
        userEmail: email,
      },
    });
  };

  const handleStep2Submit = async (step2FormData) => {
    setError(null);

    // Combine step 1 and step 2 data
    const combinedData = {
      ...step1Data,
      ...step2FormData,
    };

    setLoading((prev) => ({ ...prev, register: true }));

    // Validate email for auth already exists
    const emailExists = await fetchSignInMethodsForEmail(
      auth,
      combinedData.schoolEmail
    );
    if (emailExists.length) {
      setError("Email already in use, try another email Id.");
      setLoading((prev) => ({ ...prev, register: false }));
      return;
    }

    // GA event tracking
    GAEventTrack({
      event: "sch_register_submit",
    });

    try {
      // 1. create Auth
      const { uniqueCode } = await createAuthWithEmailAndPass(combinedData);

      // 2. send success email
      await sendSuccessMail(
        combinedData.schoolName,
        combinedData.schoolEmail,
        uniqueCode
      );

      // 3. navigate to dashboard
      navigate("/schoolDashboard");
    } catch (error) {
      console.log(error);
      setError(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, register: false }));
    }
  };

  return (
    <div className="school-register-step-container">
      {/* Progress Indicator */}
      <div className="progress-indicator mb-8">
        <div
          className={`step-indicator ${
            currentStep === 1 ? "active" : currentStep > 1 ? "completed" : ""
          }`}
        >
          <div className="step-number">1</div>
        </div>
        <div
          className={`step-connector ${currentStep > 1 ? "completed" : ""}`}
        ></div>
        <div className={`step-indicator ${currentStep === 2 ? "active" : ""}`}>
          <div className="step-number">2</div>
        </div>
      </div>

      {/* Step Content */}
      {currentStep === 1 && (
        <SchoolRegisterStep1
          contactPersonName={contactPersonName}
          setContactPersonName={setContactPersonName}
          contactPersonEmail={contactPersonEmail}
          setContactPersonEmail={setContactPersonEmail}
          contactPersonPhone={contactPersonPhone}
          setContactPersonPhone={setContactPersonPhone}
          schoolName={schoolName}
          setSchoolName={setSchoolName}
          schoolEmail={schoolEmail}
          setSchoolEmail={setSchoolEmail}
          password={password}
          setPassword={setPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          schoolFee={schoolFee}
          setSchoolFee={setSchoolFee}
          practidoFee={practidoFee}
          onNext={handleStep1Next}
          error={error}
          setError={setError}
        />
      )}

      {currentStep === 2 && (
        <SchoolRegisterStep2
          schoolAddress={schoolAddress}
          setSchoolAddress={setSchoolAddress}
          address={address}
          setAddress={setAddress}
          schoolPhone={schoolPhone}
          setSchoolPhone={setSchoolPhone}
          logoUrl={logoUrl}
          setLogoUrl={setLogoUrl}
          logoRef={logoRef}
          setLogoRef={setLogoRef}
          principalName={principalName}
          setPrincipalName={setPrincipalName}
          principalEmail={principalEmail}
          setPrincipalEmail={setPrincipalEmail}
          principalPhone={principalPhone}
          setPrincipalPhone={setPrincipalPhone}
          onBack={handleStep2Back}
          onSubmit={handleStep2Submit}
          step1Data={step1Data}
          loading={loading}
          error={error}
          setError={setError}
        />
      )}
    </div>
  );
};

export default SchoolRegisterFormStep;
